/**
 * Which AI role a prompt row is written for. `null` is the SHARED row every role falls back to,
 * so one prompt name can carry one shared row plus one row per role.
 */
export enum AiRoleEnum {
  SERVICE = "SERVICE",
  ECOMMERCE = "ECOMMERCE",
  MARKETPLACE = "MARKETPLACE"
}

export const ALL_AI_ROLES: AiRoleEnum[] = [
  AiRoleEnum.SERVICE,
  AiRoleEnum.ECOMMERCE,
  AiRoleEnum.MARKETPLACE
]

/**
 * Which engine version's copy of a prompt a row is. `null` is the BASE layer — the row every
 * engine version reads unless that version has its own copy of the prompt.
 */
export type PromptLayer = string | null

export const BASE_LAYER = null
export const BASE_LAYER_LABEL = "BASE"
/** The select needs a string for "no layer"; `null` is not a usable option value. */
export const BASE_LAYER_VALUE = "__BASE__"

export function promptLayerLabel(layer?: PromptLayer) {
  return layer ?? BASE_LAYER_LABEL
}

export function toLayerValue(layer?: PromptLayer) {
  return layer ?? BASE_LAYER_VALUE
}

export function fromLayerValue(value: string): PromptLayer {
  return value === BASE_LAYER_VALUE || value === "" ? null : value
}

/** The list rows are keyed by (name, role, engineVersion) — a name alone is not unique. */
export function promptRowKey(prompt: {
  name: string
  role?: AiRoleEnum | null
  engineVersion?: PromptLayer
}) {
  return `${prompt.name}::${prompt.role ?? "_"}::${prompt.engineVersion ?? "_"}`
}

export const SHARED_ROLE_LABEL = "All roles"

export function promptRoleLabel(role?: AiRoleEnum | null) {
  return role ?? SHARED_ROLE_LABEL
}

/**
 * The engine versions that have their own copy of one prompt name, in version order.
 *
 * This is what the BASE editor has to say out loud: an admin editing a BASE row is editing what
 * EVERY version without a copy reads, and the versions listed here will not see the edit at all.
 */
/** Versions the service runs that have no copy of this prompt yet. */
export function forkTargetsFor(
  layer: PromptLayer,
  engineVersions: ReadonlyArray<string>,
  forkedInto: ReadonlyArray<string>
): string[] {
  return engineVersions
    .filter((version) => version !== layer && !forkedInto.includes(version))
    .sort(compareEngineVersions)
}

export function layersOfPrompt(
  prompts: { name: string; engineVersion?: PromptLayer }[],
  name: string
): string[] {
  const layers = new Set<string>()
  for (const prompt of prompts) {
    if (prompt.name === name && prompt.engineVersion) {
      layers.add(prompt.engineVersion)
    }
  }
  return Array.from(layers).sort(compareEngineVersions)
}

/** `v10` sorts after `v9`, which a string compare gets wrong. */
export function compareEngineVersions(a: string, b: string) {
  const na = Number(a.replace(/^v/, ""))
  const nb = Number(b.replace(/^v/, ""))
  if (Number.isNaN(na) || Number.isNaN(nb)) return a.localeCompare(b)
  return na - nb
}

/** Every layer present in the catalogue, BASE first — the list's layer tabs. */
export function layersInUse(
  prompts: { engineVersion?: PromptLayer }[]
): PromptLayer[] {
  const layers = new Set<string>()
  for (const prompt of prompts) {
    if (prompt.engineVersion) layers.add(prompt.engineVersion)
  }
  return [BASE_LAYER, ...Array.from(layers).sort(compareEngineVersions)]
}

export enum AiPromptUsage {
  AI_AGENT = "AI_AGENT",
  AI_ASSISTANT = "AI_ASSISTANT",
  CHAT = "CHAT",
  COMMENT = "COMMENT",
  QUESTION = "QUESTION",
  TICKET = "TICKET",
  CALL = "CALL",
  OTHERS = "OTHERS"
}

export enum GPTModels {
  GPT_3_5_TURBO = "gpt-3.5-turbo",
  GPT_4_1 = "gpt-4.1",
  GPT_4o = "gpt-4o",
  GPT_4o_MINI = "gpt-4o-mini",
  "GPT_4.1_MINI" = "gpt-4.1-mini",
  "GPT_4.1_NANO" = "gpt-4.1-nano",
  "GPT-5.1" = "gpt-5.1",
  "GPT-5.2" = "gpt-5.2",
  "GPT-5.6_SOL" = "gpt-5.6-sol",
  "GPT-5.6_TERRA" = "gpt-5.6-terra",
  "GPT-5.6_LUNA" = "gpt-5.6-luna",
  "GPT-6_SOL" = "gpt-6-sol",
  "GPT-6_LUNA" = "gpt-6-luna"
}

export enum GeminiModels {
  GEMINI_2_5_FLASH_LITE = "gemini-2.5-flash-lite",
  GEMINI_2_5_FLASH = "gemini-2.5-flash",
  GEMINI_3_FLASH_PREVIEW = "gemini-3-flash-preview",
  GEMINI_3_5_FLASH = "gemini-3.5-flash",
  GEMINI_3_5_FLASH_LITE = "gemini-3.5-flash-lite"
}

// Unified AI Models enum
export const AiModels = { ...GPTModels, ...GeminiModels } as const
export type AiModels = GPTModels | GeminiModels

export const ALL_AI_MODELS: AiModels[] = [
  ...Object.values(GPTModels),
  ...Object.values(GeminiModels)
]

// Price per 1M tokens (USD), kept in sync with qpien-ai Llm/core/llm.constants.ts
const AI_MODEL_PRICE_PER_MILLION: Record<
  AiModels,
  { input: number; output: number }
> = {
  [GPTModels.GPT_3_5_TURBO]: { input: 1.5, output: 0.5 },
  [GPTModels["GPT_4.1_NANO"]]: { input: 0.1, output: 0.4 },
  [GPTModels["GPT_4.1_MINI"]]: { input: 0.4, output: 1.6 },
  [GPTModels.GPT_4o_MINI]: { input: 0.15, output: 0.6 },
  [GPTModels.GPT_4_1]: { input: 2, output: 8 },
  [GPTModels.GPT_4o]: { input: 2.5, output: 10 },
  [GPTModels["GPT-5.1"]]: { input: 1.25, output: 10 },
  [GPTModels["GPT-5.2"]]: { input: 1.75, output: 14 },
  [GPTModels["GPT-5.6_SOL"]]: { input: 5, output: 30 },
  [GPTModels["GPT-5.6_TERRA"]]: { input: 2, output: 12 },
  [GPTModels["GPT-5.6_LUNA"]]: { input: 0.2, output: 1.2 },
  [GPTModels["GPT-6_SOL"]]: { input: 2, output: 10 },
  [GPTModels["GPT-6_LUNA"]]: { input: 0.1, output: 0.5 },
  [GeminiModels.GEMINI_2_5_FLASH_LITE]: { input: 0.1, output: 0.4 },
  [GeminiModels.GEMINI_2_5_FLASH]: { input: 0.3, output: 2.5 },
  [GeminiModels.GEMINI_3_FLASH_PREVIEW]: { input: 0.5, output: 3 },
  [GeminiModels.GEMINI_3_5_FLASH]: { input: 1.5, output: 9 },
  [GeminiModels.GEMINI_3_5_FLASH_LITE]: { input: 0.3, output: 2.5 }
}

export function isGeminiOnlyPrompt(name: string) {
  return name === "ENRICH_USER_QUERY"
}

export function isGeminiModel(model: string) {
  return (Object.values(GeminiModels) as string[]).includes(model)
}

export function formatModelPrice(model: AiModels): string {
  const price = AI_MODEL_PRICE_PER_MILLION[model]
  if (!price) return model
  return `${model} ($${price.input.toFixed(2)} / $${price.output.toFixed(2)} per 1M)`
}

export type ReasoningEffort = "none" | "minimal" | "low" | "medium" | "high"

/** Per-row model call settings; `null` leaves the call site's own default. */
export interface AiPromptModelSettings {
  reasoningEffort?: ReasoningEffort | null
  maxOutputTokens?: number | null
}

export const PROMPT_MIN_OUTPUT_TOKENS = 256
export const PROMPT_MAX_OUTPUT_TOKENS = 20000

/**
 * The efforts a row may pin for its model — only the gpt-6 / gpt-5.6 contract is modelled, which
 * has no `minimal` tier. Kept in sync with qpien-ai `promptReasoningEffortsFor`.
 */
export function promptReasoningEffortsFor(model: string): ReasoningEffort[] {
  return model.startsWith("gpt-6") || model.startsWith("gpt-5.6")
    ? ["none", "low", "medium", "high"]
    : []
}

/** The form keeps an object so nested fields bind; the API stores `null` when nothing is pinned. */
export function toModelSettingsPayload(
  model: string,
  settings?: AiPromptModelSettings | null
): AiPromptModelSettings | null {
  const effort = settings?.reasoningEffort ?? null
  const reasoningEffort =
    effort && promptReasoningEffortsFor(model).includes(effort) ? effort : null
  const tokens = settings?.maxOutputTokens
  const maxOutputTokens =
    tokens === null || tokens === undefined || String(tokens) === ""
      ? null
      : Number(tokens)
  if (reasoningEffort === null && maxOutputTokens === null) return null
  return { reasoningEffort, maxOutputTokens }
}

interface PromptDraftShape {
  description?: string
  role?: string | null
  engineVersion?: PromptLayer
  usage?: string
  aiModel?: string
  responseSchema?: unknown
  modelSettings?: AiPromptModelSettings | null
}

/**
 * `isDirty` also flips for edits that save nothing — a number field typed into and cleared holds
 * "" where the row holds null — which left "Review changes" enabled over an empty diff.
 */
export function draftDiffersFromSaved(
  saved: PromptDraftShape,
  draft: PromptDraftShape
): boolean {
  return (
    (saved.description ?? "") !== (draft.description ?? "") ||
    (saved.role ?? null) !== (draft.role ?? null) ||
    (saved.engineVersion ?? null) !== (draft.engineVersion ?? null) ||
    saved.usage !== draft.usage ||
    saved.aiModel !== draft.aiModel ||
    JSON.stringify(saved.responseSchema ?? null) !==
      JSON.stringify(draft.responseSchema ?? null) ||
    formatModelSettings(saved.modelSettings) !==
      formatModelSettings(
        toModelSettingsPayload(draft.aiModel ?? "", draft.modelSettings)
      )
  )
}

export function formatModelSettings(settings?: AiPromptModelSettings | null) {
  if (!settings) return "default"
  const parts = [
    settings.reasoningEffort ? `reasoning: ${settings.reasoningEffort}` : "",
    settings.maxOutputTokens ? `max tokens: ${settings.maxOutputTokens}` : ""
  ].filter(Boolean)
  return parts.length ? parts.join(", ") : "default"
}

/**
 * Reasoning models (gpt-5.x, gpt-6, o-series) reject any temperature other than the
 * default 1. Kept in sync with qpien-ai Llm/core/llm.constants.ts.
 */
export function supportsCustomTemperature(model: string): boolean {
  if (/^o\d/.test(model) || model.startsWith("gpt-6")) return false
  return !(model.startsWith("gpt-5") && !model.startsWith("gpt-5-chat"))
}
